import { BadgeCheck, CreativeCommons, FileText, Map, ScanBarcode } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PRODUCTS, getActiveProduct } from '../utils/urlConfig';

const BottomNavbar = ({ theme, isDark }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId: urlProductId, serialNumber: urlSerialNumber, sno: urlSno, id: verificationId } = useParams();
  const effectiveSerialNumber = urlSerialNumber || urlSno;
  
  const getProductPath = () => {
    if (urlProductId && effectiveSerialNumber) {
      return { productId: urlProductId, serialNumber: effectiveSerialNumber };
    }
    // Fallback to active product from localStorage
    const activeProduct = getActiveProduct();
    const product = PRODUCTS[activeProduct];
    if (product) {
      return { productId: product.gtin, serialNumber: product.serialNumber };
    }
    return null;
  };

  const productPath = getProductPath();

  const getRoutePath = (routeName) => {
    if (!productPath) return `/${routeName}`;

    // Preserve verification status and serial when navigating to verification page
    if (routeName === 'verification') {
      const savedStatus = localStorage.getItem('lastVerificationStatus');
      const savedSerial = localStorage.getItem('lastVerificationSerial');
      // Use saved serial if available (handles suspected/expired serials), otherwise use URL serial
      const serial = savedSerial || productPath.serialNumber;
      const basePath = `/01/${productPath.productId}/21/${serial}/${routeName}`;
      if (verificationId) {
        return `${basePath}/${verificationId}`;
      }
      if (savedStatus) {
        return `${basePath}/${savedStatus}`;
      }
      return basePath;
    }

    return `/01/${productPath.productId}/21/${productPath.serialNumber}/${routeName}`;
  };

  const navItems = [
    { 
      id: 'verification', 
      label: 'Verification', 
      icon: BadgeCheck, 
      path: 'verification'
    },
    { 
      id: 'dpp', 
      label: 'DPP', 
      icon: FileText, 
      path: 'dpp'
    },
    { 
      id: 'cc', 
      label: 'CC', 
      icon: CreativeCommons, 
      path: 'ccmap'
    },
    { 
      id: 'map', 
      label: 'Map', 
      icon: Map, 
      path: 'map'
    },
    { 
      id: 'gs1', 
      label: 'GS1 DL', 
      icon: ScanBarcode, 
      path: 'gs1'
    }
  ];

  const handleNavigation = (routeName) => {
    const fullPath = getRoutePath(routeName);
    navigate(fullPath, { state: { isDark } });
  };

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden shadow-lg"
      style={{ backgroundColor: theme.background2, boxShadow: '0 -8px 16px 0px rgba(0, 0, 0, 0.12), 0 -4px 8px 0px rgba(0, 0, 0, 0.08)' }}
    >
      <div 
        className="flex justify-around items-center px-2 py-3 border-t"
        style={{ borderColor: theme.stroke }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const fullPath = getRoutePath(item.path);
          // For verification route, check if current path starts with base verification path
          // For other routes, require exact match
          const isActive = item.id === 'verification' 
            ? location.pathname.startsWith(`/01/${productPath?.productId}/21/${productPath?.serialNumber}/verification`)
            : location.pathname === fullPath;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className="flex flex-col items-center justify-center min-w-0 py-1 transition-all relative"
            >
              <Icon 
                size={24} 
                style={{ 
                  color: isActive ? theme.button : theme.subText,
                  strokeWidth: isActive ? 2 : 1.5
                }}
              />
              <span 
                className="text-xs mt-1.5 font-normal"
                style={{ 
                  color: isActive ? theme.button : theme.subText 
                }}
              >
                {item.label}
              </span>
              {isActive && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: theme.button }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavbar;
